import { useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Label,
  Select,
  TextInput,
  toast,
} from '@pella/sash-framework';
import { useUser } from '@pella/react-layout';

import {
  useDistAcctByCC,
  useMROOrganizations,
  useSubInventories,
  useInvLocations,
} from '../../../hooks/useInvManagement';
import { useUserMutation } from '../../../hooks';
import ChartOfAccountSelector from '../ChartOfAccountSelector';
import styles from './PlantMROSubInvSelector.module.css';

/**
 * PlantMROSubInvSelector component for selecting Plant, MRO Sub Inventory, and destination account.
 * @param {object} root0 - Component props
 * @param {string} [root0.defaultDestinationAcct] - Default destination account value
 * @param {(value: { settings: object, distributionAccount: string | undefined, distributionAccountId: string | undefined, validated: boolean }) => void} root0.selectedValueOnChange - Callback fired when any selection changes
 * @param {(active: boolean) => void} root0.COALLookupActive - Callback fired when COA lookup dialog state changes
 * @param {boolean} [root0.showSavePreferences] - Whether to show save preferences button
 * @param {boolean} [root0.defaultExpanded] - Whether the accordion is expanded by default
 * @returns {import('react').ReactElement} The PlantMROSubInvSelector component
 */
const PlantMROSubInvSelector = ({
  defaultDestinationAcct,
  selectedValueOnChange,
  COALLookupActive,
  showSavePreferences = true,
  defaultExpanded = false,
}) => {
  const { appPreferences } = useUser();
  const { addTeamMemberAppPref } = useUserMutation();

  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedSubInvCode, setSelectedSubInvCode] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [destinationAcct, setDestinationAcct] = useState('');
  const [validationTrigger, setValidationTrigger] = useState('');
  const [destAcctIsValidated, setDestAcctIsValidated] = useState(false);
  const [distributionAccount, setDistributionAccount] = useState(null);
  const [showCOALookup, setShowCOALookup] = useState(false);

  // Pre-fill tracking — state (not refs) so they can be read during render
  const [orgPrefApplied, setOrgPrefApplied] = useState(false);
  const [subInvPrefApplied, setSubInvPrefApplied] = useState(false);
  const [locationPrefApplied, setLocationPrefApplied] = useState(false);
  const [destAcctPrefApplied, setDestAcctPrefApplied] = useState(false);

  // Track previous org for sub-inv clear logic
  const [prevOrgId, setPrevOrgId] = useState('');
  // Track previous dist acct query results for validation logic
  const [prevDistAcctKey, setPrevDistAcctKey] = useState(null);

  // Stable ref for selectedValueOnChange — updated synchronously after each render so the effect
  // always calls the latest prop without listing it in the dep array (prop identity changes every parent render)
  const selectedValueOnChangeRef = useRef(selectedValueOnChange);
  useLayoutEffect(() => {
    selectedValueOnChangeRef.current = selectedValueOnChange;
  }, [selectedValueOnChange]);

  const { mroOrgs, mroOrgsIsLoading } = useMROOrganizations();
  const { subInventories, subInventoriesIsLoading } = useSubInventories(selectedOrgId);
  const { invLocations } = useInvLocations();
  const { data: distAcctData, isFetched: distAcctFetched } = useDistAcctByCC(validationTrigger);

  const moveOrderPrefs = useMemo(
    () => appPreferences?.preferences?.find((p) => p.prefId === 'MoveOrder')?.preference?.apps ?? null,
    [appPreferences],
  );

  // --- Render-time state adjustments (replaces pre-fill and sync effects) ---

  // Pre-fill org once mroOrgs data is available
  if (!orgPrefApplied && moveOrderPrefs?.MoveOrderMROOrg && mroOrgs?.length) {
    setOrgPrefApplied(true);
    const orgId = String(moveOrderPrefs.MoveOrderMROOrg);
    setSelectedOrgId(orgId);
    setPrevOrgId(orgId); // prevent sub-inv clear for pre-fill
  }

  // Pre-fill sub-inv once subInventories data is available for the pre-filled org
  if (
    !subInvPrefApplied &&
    moveOrderPrefs?.MoveOrderSubInvLoc &&
    subInventories?.length &&
    selectedOrgId === String(moveOrderPrefs?.MoveOrderMROOrg ?? '')
  ) {
    setSubInvPrefApplied(true);
    setSelectedSubInvCode(String(moveOrderPrefs.MoveOrderSubInvLoc));
  }

  // Pre-fill ship-to location once invLocations data is available
  if (!locationPrefApplied && moveOrderPrefs?.MoveOrderShipToLoc && invLocations?.length) {
    setLocationPrefApplied(true);
    setSelectedLocationId(String(moveOrderPrefs.MoveOrderShipToLoc));
  }

  // Pre-fill destination account
  if (!destAcctPrefApplied) {
    const acct = moveOrderPrefs?.MoveOrderDestinationAcct || defaultDestinationAcct;
    if (acct) {
      setDestAcctPrefApplied(true);
      setDestinationAcct(acct);
      setValidationTrigger(acct);
    }
  }

  // Clear sub-inventory when org changes (user-driven, not pre-fill)
  if (selectedOrgId !== prevOrgId) {
    setPrevOrgId(selectedOrgId);
    if (orgPrefApplied) {
      setSelectedSubInvCode('');
    }
  }

  // Derive validation state from dist acct query results
  const distAcctKey = distAcctFetched && validationTrigger ? validationTrigger : null;
  if (distAcctKey && distAcctKey !== prevDistAcctKey) {
    setPrevDistAcctKey(distAcctKey);
    if (distAcctData && distAcctData.length > 0) {
      setDistributionAccount(distAcctData[0]);
      setDestAcctIsValidated(true);
    } else {
      setDistributionAccount(null);
      setDestAcctIsValidated(false);
    }
  }

  // Toast for failed validation (side effect, requires effect)
  useEffect(() => {
    if (distAcctFetched && validationTrigger && (!distAcctData || distAcctData.length === 0)) {
      toast.error('Current Destination Account was not found!');
    }
  }, [distAcctFetched, validationTrigger, distAcctData]);

  const formIsValid = useMemo(
    () =>
      Boolean(selectedOrgId && selectedSubInvCode && selectedLocationId && (!destinationAcct || destAcctIsValidated)),
    [selectedOrgId, selectedSubInvCode, selectedLocationId, destinationAcct, destAcctIsValidated],
  );

  // Emit full value to parent on any change — uses ref so prop identity changes don't require a disable
  useEffect(() => {
    selectedValueOnChangeRef.current({
      settings: {
        MoveOrderMROOrg: selectedOrgId,
        MoveOrderSubInvLoc: selectedSubInvCode,
        MoveOrderShipToLoc: selectedLocationId,
        MoveOrderDestinationAcct: destinationAcct,
      },
      distributionAccount: distributionAccount?.distributionAccount,
      distributionAccountId: distributionAccount?.distributionAccountId,
      validated: formIsValid,
    });
  }, [selectedOrgId, selectedSubInvCode, selectedLocationId, destinationAcct, distributionAccount, formIsValid]);

  // Call COALLookupActive directly in handlers rather than watching showCOALookup via effect.
  // Defined before handleCOASelection and handleClearAcct which reference closeCOALookup.
  const openCOALookup = () => {
    setShowCOALookup(true);
    COALLookupActive(true);
  };

  const closeCOALookup = () => {
    setShowCOALookup(false);
    COALLookupActive(false);
  };

  const handleClearAcct = () => {
    setDestinationAcct('');
    setDestAcctIsValidated(false);
    setDistributionAccount(null);
    setValidationTrigger('');
  };

  const handleCOASelection = (value) => {
    setDestinationAcct(value);
    setDestAcctIsValidated(false);
    setDistributionAccount(null);
    closeCOALookup();
    if (value.length === 32) {
      setValidationTrigger(value);
    }
  };

  const handleSavePreferences = async () => {
    try {
      await addTeamMemberAppPref.mutateAsync({
        preference: {
          apps: {
            MoveOrderMROOrg: selectedOrgId,
            MoveOrderSubInvLoc: selectedSubInvCode,
            MoveOrderShipToLoc: selectedLocationId,
            MoveOrderDestinationAcct: destinationAcct || null,
          },
        },
        prefId: 'MoveOrder',
      });
      toast.success('Preferences saved!');
    } catch {
      // error toast handled by useUserMutation onError
    }
  };

  if (showCOALookup) {
    return (
      <ChartOfAccountSelector
        selectionOnClick={handleCOASelection}
        cancelOnClick={closeCOALookup}
        defaultDestinationAcct={destinationAcct}
      />
    );
  }

  return (
    <Accordion variant='box'>
      <AccordionItem defaultOpen={defaultExpanded}>
        <AccordionTrigger>
          <span className={styles.accordionTriggerContent}>
            MRO Settings
            <Badge color={formIsValid ? 'success' : 'error'}>{formIsValid ? 'Valid' : 'Invalid'}</Badge>
          </span>
        </AccordionTrigger>
        <AccordionContent>
          <Select
            id='mro-org'
            labelText='MRO Organization'
            value={selectedOrgId}
            disabled={mroOrgsIsLoading}
            onChange={(e) => setSelectedOrgId(e.target.value)}
          >
            <option value=''>Choose organization...</option>
            {(mroOrgs ?? []).map((org) => {
              const anyOrg = /** @type {{organizationId: string, description: string}} */ (org);
              return (
                <option key={anyOrg.organizationId} value={anyOrg.organizationId}>
                  ({anyOrg.organizationId}) {anyOrg.description}
                </option>
              );
            })}
          </Select>

          <Select
            id='sub-inv'
            labelText='Sub Inventory Location'
            value={selectedSubInvCode}
            disabled={!selectedOrgId || subInventoriesIsLoading}
            onChange={(e) => setSelectedSubInvCode(e.target.value)}
          >
            <option value=''>Choose sub-inventory...</option>
            {(subInventories ?? []).map((sub) => (
              <option key={sub.subinventoryCode} value={sub.subinventoryCode}>
                ({sub.subinventoryCode}) {sub.description}
              </option>
            ))}
          </Select>

          <Select
            id='ship-to'
            labelText='Ship To Location'
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
          >
            <option value=''>Choose ship-to location...</option>
            {(invLocations ?? []).map((loc) => (
              <option key={loc.locationId} value={loc.locationId}>
                ({loc.locationId}) {loc.description}
              </option>
            ))}
          </Select>

          <div className={styles.destAcctRow}>
            <div className={styles.destAcctFlex}>
              <TextInput id='dest-acct' labelText='Destination Account' value={destinationAcct} readOnly />
            </div>
            <Button aria-label='clear' iconLeft='clear' onClick={handleClearAcct} />
            <Button aria-label='lookup' iconLeft='search' onClick={openCOALookup} />
          </div>

          <div className={styles.noteText}>
            <Label>**Clear (X) Destination Account to allow Equipment Cost Center to be utilized in Move Order</Label>
          </div>

          {showSavePreferences && (
            <div className={styles.savePrefButton}>
              <Button
                size='sm'
                color='secondary'
                disabled={!formIsValid || addTeamMemberAppPref.isPending}
                onClick={handleSavePreferences}
              >
                Save as my preferences
              </Button>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default PlantMROSubInvSelector;
